/**
 * useAuth — pure Redux selector + action dispatchers.
 *
 * This hook contains ZERO useEffect / subscriptions. All auth lifecycle
 * side-effects (onAuthStateChange, getSession, profile fetch) are owned
 * by the singleton <AuthProvider> mounted once in App.tsx.
 *
 * Components call useAuth() solely to:
 *  1. Read current auth/profile/company state from Redux
 *  2. Invoke auth actions (signIn, signOut, resetPassword, etc.)
 */

import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  setProfile,
  setProfileError,
  clearAuth,
  setPasswordRecovery,
} from '@/store/slices/authSlice';
import { setCurrentCompany, setCompanyError, clearCompany } from '@/store/slices/companySlice';
import { RootState } from '@/store/store';
import { clearUserSpecificCaches } from '@/utils/authCacheUtils';

export const useAuth = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const authState = useSelector((state: RootState) => state.auth);
  const companyState = useSelector((state: RootState) => state.company);

  // Provide safe defaults to prevent null reference errors during navigation transitions
  const { user, session, loading, profile, profileError, isPasswordRecovery } = authState || {
    user: null,
    session: null,
    loading: true,
    profile: null,
    profileError: null,
    isPasswordRecovery: false,
  };
  const { currentCompany, companyError } = companyState || {
    currentCompany: null,
    companyError: null,
  };

  const retryProfileFetch = async () => {
    if (!user) return;
    dispatch(setProfileError(''));
    try {
      const { data: profileData, error: profileFetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (profileFetchError) {
        dispatch(setProfileError(profileFetchError.message || 'Failed to load profile'));
        dispatch(setProfile(null));
        return;
      }

      if (profileData) {
        dispatch(setProfile(profileData));
        if (profileData.company_id) {
          const { data: companyData } = await supabase
            .from('companies')
            .select('*')
            .eq('id', profileData.company_id)
            .single();
          if (companyData) dispatch(setCurrentCompany(companyData));
        }
      }
    } catch (err) {
      dispatch(setProfileError('Unexpected error loading profile'));
    }
  };

  const retryCompanyFetch = async () => {
    if (!user) return;
    dispatch(setCompanyError(null));
    try {
      const { data: profileData, error: profileFetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (profileFetchError || !profileData) {
        dispatch(setCompanyError(profileFetchError?.message || 'Failed to load profile'));
        return;
      }

      dispatch(setProfile(profileData));

      if (!profileData.company_id) {
        dispatch(setCompanyError('No company is linked to this account'));
        return;
      }

      const { data: companyData, error: companyFetchError } = await supabase
        .from('companies')
        .select('*')
        .eq('id', profileData.company_id)
        .single();

      if (companyData && !companyFetchError) {
        dispatch(setCurrentCompany(companyData));
      } else {
        dispatch(
          setCompanyError(companyFetchError?.message || 'Unable to load your workspace details'),
        );
      }
    } catch {
      dispatch(setCompanyError('Unexpected error loading your workspace'));
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signUp = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    companyName: string,
    accessCode?: string,
  ) => {
    const redirectUrl = `${window.location.origin}/`;
    const metadata: Record<string, string> = {
      first_name: firstName,
      last_name: lastName,
      company_name: companyName,
      tos_accepted_at: new Date().toISOString(),
    };
    if (accessCode && accessCode.trim()) {
      metadata.access_code = accessCode.trim();
    }
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectUrl, data: metadata },
    });
    return { data, error };
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: redirectUrl });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (!error) {
      dispatch(setPasswordRecovery(false));
    }
    return { error };
  };

  const clearRecoveryMode = () => {
    dispatch(setPasswordRecovery(false));
  };

  const signOut = async () => {
    try {
      clearUserSpecificCaches(queryClient);
      supabase.removeAllChannels();
      dispatch(clearAuth());
      dispatch(clearCompany());
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error during sign out:', error);
        return { error };
      }
      return { error: null };
    } catch (err) {
      console.error('Unexpected error during sign out:', err);
      dispatch(clearAuth());
      dispatch(clearCompany());
      return { error: err };
    }
  };

  return {
    user,
    session,
    loading,
    profile,
    profileError,
    currentCompany,
    companyError,
    isPasswordRecovery,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    clearRecoveryMode,
    retryProfileFetch,
    retryCompanyFetch,
  };
};
